import type { FlowNode, SendMsgDingTalkRobotConfig } from '@lighthouse/shared'
import { getAllCanBeUpstreamNodes, getUpstreamNodesOptions, PAGE_LINK_OPTION, useAtomData } from '@lighthouse/shared'
import { useNodesData } from '@xyflow/react'
import React, { useCallback, useMemo } from 'react'

import { lastPageOfStackAtom } from '@/atoms/page/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'

import * as CM from '../styles'
import { extractDataFn, textRichVariableName } from '../util'
import * as SC from './styles'

type LinkMsgProps = {
    link: SendMsgDingTalkRobotConfig['link']
}

const LinkMsg: React.FC<LinkMsgProps> = ({ link }) => {
    // const { editorTitle, editorText, picUrl, editorMessageUrl} = link;
    const { type } = useFlowContainerContext()
    const isInFlow = !!type
    const isNotAutomation = type !== 'automation'
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const [stackId, pageId] = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => [s?.stackId || '', s?.pageId || ''], [])
    )

    const userOption = useGetUserRecordOptions()
    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })

    const extractDataTitle = useMemo(() => extractDataFn(link?.editorTitle), [link?.editorTitle])
    const extractDataText = useMemo(() => extractDataFn(link?.editorText), [link?.editorText])
    const extractDataUrl = useMemo(() => extractDataFn(link?.editorMessageUrl), [link?.editorMessageUrl])

    const nodesEditorTitle = useNodesData(extractDataTitle.nodeIds) as FlowNode[]
    const nodesEditorText = useNodesData(extractDataText.nodeIds) as FlowNode[]
    const nodesEditorUrl = useNodesData(extractDataUrl?.nodeIds) as FlowNode[]

    const upstreamOptionsTitle = useMemo(
        () =>
            isInFlow
                ? getUpstreamNodesOptions({
                      workflowList,
                      dataSourceList,
                      upstreamNodes: getAllCanBeUpstreamNodes(nodesEditorTitle || []),
                      allUpstreamNodes: nodesEditorTitle
                  })
                : undefined,
        [isInFlow, workflowList, dataSourceList, nodesEditorTitle]
    )

    const upstreamOptionsText = useMemo(
        () =>
            isInFlow
                ? getUpstreamNodesOptions({
                      workflowList,
                      dataSourceList,
                      upstreamNodes: getAllCanBeUpstreamNodes(nodesEditorText || []),
                      allUpstreamNodes: nodesEditorText
                  })
                : undefined,
        [isInFlow, workflowList, dataSourceList, nodesEditorText]
    )

    const upstreamOptionsUrl = useMemo(
        () =>
            isInFlow
                ? getUpstreamNodesOptions({
                      workflowList,
                      dataSourceList,
                      upstreamNodes: getAllCanBeUpstreamNodes(nodesEditorUrl || []),
                      allUpstreamNodes: nodesEditorUrl
                  })
                : undefined,
        [isInFlow, workflowList, dataSourceList, nodesEditorUrl]
    )
    if (!extractDataText.objects.length && !extractDataTitle.objects.length && !extractDataUrl.objects.length) {
        return null
    }
    return (
        <CM.CardItemContainer>
            {extractDataTitle.objects.length > 0 && (
                <>
                    <SC.TypeName>消息标题</SC.TypeName>
                    <CM.FieldItemContent>
                        <CM.FieldItemTitle>
                            {extractDataTitle.objects.map(item => {
                                if (item.type === 'text') {
                                    return <SC.Text key={item.value}>{item.value}</SC.Text>
                                }
                                const text = textRichVariableName({
                                    userOption,
                                    upstreamOptions: upstreamOptionsTitle,
                                    pageOptions: isNotAutomation ? [PAGE_LINK_OPTION, ...pageOptions] : undefined,
                                    data: item
                                })
                                return (
                                    <SC.Text key={text} color="var(--color-purple-500)">
                                        {text}
                                    </SC.Text>
                                )
                            })}
                        </CM.FieldItemTitle>
                    </CM.FieldItemContent>
                </>
            )}

            {extractDataText && extractDataText.objects.length > 0 && (
                <>
                    <SC.TypeName>消息简介</SC.TypeName>
                    <CM.FieldItemContent>
                        <CM.FieldItemTitle>
                            {extractDataText.objects.map(item => {
                                if (item.type === 'text') {
                                    return <SC.Text key={item.value}>{item.value}</SC.Text>
                                }
                                const text = textRichVariableName({
                                    userOption,
                                    upstreamOptions: upstreamOptionsText,
                                    pageOptions: isNotAutomation ? [PAGE_LINK_OPTION, ...pageOptions] : undefined,
                                    data: item
                                })
                                return (
                                    <SC.Text key={text} color="var(--color-purple-500)">
                                        {text}
                                    </SC.Text>
                                )
                            })}
                        </CM.FieldItemTitle>
                    </CM.FieldItemContent>
                </>
            )}

            {extractDataUrl && extractDataUrl.objects.length > 0 && (
                <>
                    <SC.TypeName>跳转链接</SC.TypeName>
                    <CM.FieldItemContent>
                        <CM.FieldItemTitle>
                            {extractDataUrl.objects.map(item => {
                                if (item.type === 'text') {
                                    return <SC.Text key={item.value}>{item.value}</SC.Text>
                                }
                                const text = textRichVariableName({
                                    userOption,
                                    upstreamOptions: upstreamOptionsUrl,
                                    pageOptions: isNotAutomation ? [PAGE_LINK_OPTION, ...pageOptions] : undefined,
                                    data: item
                                })
                                return (
                                    <SC.Text key={text} color="var(--color-purple-500)">
                                        {text}
                                    </SC.Text>
                                )
                            })}
                        </CM.FieldItemTitle>
                    </CM.FieldItemContent>
                </>
            )}
        </CM.CardItemContainer>
    )
}

export default LinkMsg
